import React, { useState, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import config from '../config/config';
import SbomList from './SbomList';
import VulnerabilityView from './VulnerabilityView';
import LicenseView from './LicenseView';
import SbomDiff from './SbomDiff';
import PackageHealthView from './PackageHealthView';
import PolicyView from './PolicyView';
import AiAssistantPanel from './ai/AiAssistantPanel';
import TrendView from './TrendView';
import SbomQuality from './SbomQuality';
import VexView from './VexView';
import RemediationView from './RemediationView';
import DependencyGraph from './DependencyGraph';
import ComplianceView from './ComplianceView';
import MalwareView from './MalwareView';
import CsafView from './CsafView';
import CraReportView from './CraReportView';
import SlsaProvenanceView from './SlsaProvenanceView';
import AnomalyDetectionView from './AnomalyDetectionView';
import ReachabilityView from './ReachabilityView';
import IntelligenceView from './IntelligenceView';
import RepoCodeAnalysis from './RepoCodeAnalysis';
import RepoSastView from './RepoSastView';
import EmptyState from './common/EmptyState';
import ErrorBoundary from './common/ErrorBoundary';
import WorkflowProgress from './common/WorkflowProgress';
import AnalysisOverview from './AnalysisOverview';
import GitHubPRCommentModal from './GitHubPRCommentModal';
import { authUrl } from '../utils/api';
import { resolveSbomFileName } from '../utils/sbom';

// ── 6 main tabs ──────────────────────────────────────────────────────────────
const MAIN_TABS = [
  { id: 'overview',        icon: '📊', labelKey: 'repoDetails.tabs.overview' },
  { id: 'vulnerabilities', icon: '🔒', labelKey: 'repoDetails.tabs.vulnerabilities' },
  { id: 'dependencies',    icon: '📦', labelKey: 'repoDetails.tabs.dependencies' },
  { id: 'compliance',      icon: '⚖️', labelKey: 'repoDetails.tabs.complianceTab' },
  { id: 'codeanalysis',    icon: '🔬', labelKey: 'repoDetails.tabs.codeAnalysis' },
  { id: 'history',         icon: '📈', labelKey: 'repoDetails.tabs.history' },
];

const MAIN_TAB_IDS = new Set(MAIN_TABS.map((t) => t.id));

// ── Sub-tabs per main tab ────────────────────────────────────────────────────
const SUB_TABS = {
  vulnerabilities: [
    { id: 'cves',         icon: '🛡️', labelKey: 'repoDetails.subtabs.cves' },
    { id: 'malware',      icon: '🦠', labelKey: 'repoDetails.subtabs.malware' },
    { id: 'csaf',         icon: '📋', labelKey: 'repoDetails.subtabs.csaf' },
    { id: 'reachability', icon: '🧭', labelKey: 'repoDetails.subtabs.reachability' },
    { id: 'vex',          icon: '🔏', labelKey: 'repoDetails.subtabs.vex' },
    { id: 'remediation',  icon: '📋', labelKey: 'repoDetails.subtabs.remediation' },
  ],
  dependencies: [
    { id: 'health',    icon: '🏥', labelKey: 'repoDetails.subtabs.health' },
    { id: 'anomalies', icon: '⚠️', labelKey: 'repoDetails.subtabs.anomalies' },
    { id: 'graph',     icon: '🕸️', labelKey: 'repoDetails.subtabs.graph' },
  ],
  compliance: [
    { id: 'licenses',   icon: '📄', labelKey: 'repoDetails.subtabs.licenses' },
    { id: 'policy',     icon: '📋', labelKey: 'repoDetails.subtabs.policy' },
    { id: 'cra',        icon: '🇪🇺', labelKey: 'repoDetails.subtabs.cra' },
    { id: 'slsa',       icon: '🔐', labelKey: 'repoDetails.subtabs.slsa' },
    { id: 'frameworks', icon: '🏛️', labelKey: 'repoDetails.subtabs.frameworks' },
  ],
  codeanalysis: [
    { id: 'intelligence', icon: '🧠', labelKey: 'repoDetails.subtabs.intelligence' },
    { id: 'codeql',       icon: '🔍', labelKey: 'repoDetails.subtabs.codeql' },
    { id: 'sast',         icon: '🛡️', labelKey: 'repoDetails.subtabs.sast' },
  ],
  history: [
    { id: 'quality', icon: '🏆', labelKey: 'repoDetails.subtabs.quality' },
    { id: 'trend',   icon: '📈', labelKey: 'repoDetails.subtabs.trend' },
    { id: 'diff',    icon: '🔀', labelKey: 'repoDetails.subtabs.diff' },
    { id: 'sboms',   icon: '📦', labelKey: 'repoDetails.subtabs.sboms' },
  ],
};

const DEFAULT_SUB = {
  vulnerabilities: 'cves',
  dependencies:    'health',
  compliance:      'licenses',
  codeanalysis:    'intelligence',
  history:         'quality',
};

// Legacy tab IDs → new {tab, sub} — keeps old URLs/deep-links working
const LEGACY_MAP = {
  sboms:        { tab: 'history',         sub: 'sboms' },
  security:     { tab: 'vulnerabilities', sub: 'cves' },
  intelligence: { tab: 'codeanalysis',    sub: 'intelligence' },
  malware:      { tab: 'vulnerabilities', sub: 'malware' },
  csaf:         { tab: 'vulnerabilities', sub: 'csaf' },
  reachability: { tab: 'vulnerabilities', sub: 'reachability' },
  vex:          { tab: 'vulnerabilities', sub: 'vex' },
  remediation:  { tab: 'vulnerabilities', sub: 'remediation' },
  health:       { tab: 'dependencies',    sub: 'health' },
  anomalies:    { tab: 'dependencies',    sub: 'anomalies' },
  graph:        { tab: 'dependencies',    sub: 'graph' },
  licenses:     { tab: 'compliance',      sub: 'licenses' },
  policy:       { tab: 'compliance',      sub: 'policy' },
  cra:          { tab: 'compliance',      sub: 'cra' },
  slsa:         { tab: 'compliance',      sub: 'slsa' },
  quality:      { tab: 'history',         sub: 'quality' },
  trend:        { tab: 'history',         sub: 'trend' },
  diff:         { tab: 'history',         sub: 'diff' },
};

// (tab/sub) combos that need an SBOM to be selected
const SBOM_REQUIRED = new Set([
  'overview',
  'vulnerabilities/cves', 'vulnerabilities/malware', 'vulnerabilities/csaf', 'vulnerabilities/reachability',
  'vulnerabilities/vex',  'vulnerabilities/remediation',
  'dependencies/health',  'dependencies/anomalies', 'dependencies/graph',
  'compliance/licenses',  'compliance/policy', 'compliance/cra',
  'compliance/slsa',      'compliance/frameworks',
  'codeanalysis/intelligence',
  'codeanalysis/codeql',
  'codeanalysis/sast',
  'history/quality',
]);

const EMPTY_ICONS = {
  overview:                       '📊',
  'vulnerabilities/cves':         '🛡️',
  'vulnerabilities/malware':      '🦠',
  'vulnerabilities/csaf':         '📋',
  'vulnerabilities/reachability': '🧭',
  'vulnerabilities/vex':          '🔏',
  'vulnerabilities/remediation':  '📋',
  'dependencies/health':          '🏥',
  'dependencies/anomalies':       '⚠️',
  'dependencies/graph':           '🕸️',
  'compliance/licenses':          '📄',
  'compliance/policy':            '📋',
  'compliance/cra':               '🇪🇺',
  'compliance/slsa':              '🔐',
  'compliance/frameworks':        '🏛️',
  'codeanalysis/intelligence':    '🧠',
  'codeanalysis/codeql':          '🔍',
  'codeanalysis/sast':            '🛡️',
  'history/quality':              '🏆',
};

// Maps current-key → legacy emptyState i18n key (reuses existing translations)
const EMPTY_I18N_KEY = {
  overview:                       'overview',
  'vulnerabilities/cves':         'security',
  'vulnerabilities/malware':      'malware',
  'vulnerabilities/csaf':         'csaf',
  'vulnerabilities/reachability': 'reachability',
  'vulnerabilities/vex':          'vex',
  'vulnerabilities/remediation':  'remediation',
  'dependencies/health':          'health',
  'dependencies/anomalies':       'anomalies',
  'dependencies/graph':           'graph',
  'compliance/licenses':          'licenses',
  'compliance/policy':            'policy',
  'compliance/cra':               'cra',
  'compliance/slsa':              'slsa',
  'compliance/frameworks':        'compliance',
  'codeanalysis/intelligence':    'intelligence',
  'codeanalysis/codeql':          'intelligence',
  'codeanalysis/sast':            'intelligence',
  'history/quality':              'quality',
};

const SHARED_EMPTY = Object.freeze({ vulns: null, health: null, licenses: null, quality: null, compliance: null, remediation: null });

function RepoDetailsTabs({ repoName }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasExplicitTab = searchParams.has('tab');
  const rawTab = searchParams.get('tab') || 'overview';
  const rawSub = searchParams.get('sub') || '';

  // Resolve legacy tab IDs → new {tab, sub}
  const resolved = !MAIN_TAB_IDS.has(rawTab) ? (LEGACY_MAP[rawTab] || null) : null;
  const activeTab = resolved ? resolved.tab : (MAIN_TAB_IDS.has(rawTab) ? rawTab : 'overview');
  const activeSub = resolved ? resolved.sub : (rawSub || DEFAULT_SUB[activeTab] || '');
  const currentKey = SUB_TABS[activeTab] ? `${activeTab}/${activeSub}` : activeTab;

  const selectedSbomId = searchParams.get('sbom') || '';
  const [sboms, setSboms] = useState([]);
  const [sbomsLoading, setSbomsLoading] = useState(true);
  const [healthStats, setHealthStats] = useState(null);
  const [sharedData, setSharedData] = useState(SHARED_EMPTY);
  const [hasVulns, setHasVulns] = useState(false);
  const [hasPolicy, setHasPolicy] = useState(false);
  const [showPRModal, setShowPRModal] = useState(false);
  const handleHealthData = useCallback((data) => setHealthStats(data), []);

  const normalizedSboms = sboms.map((sbom) => ({
    ...sbom,
    CommitHash: sbom.CommitHash || sbom.commitHash,
    SbomLink: sbom.SbomLink || sbom.sbomLink,
  }));
  const fallbackSbomId = normalizedSboms[0]?.CommitHash || '';
  const effectiveSelectedSbomId = selectedSbomId || fallbackSbomId;
  const selectedSbomFileName = resolveSbomFileName(effectiveSelectedSbomId, normalizedSboms);

  const setTab = useCallback((tabId, subId) => {
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      // Handle legacy IDs forwarded from AnalysisOverview callbacks
      const leg = !MAIN_TAB_IDS.has(tabId) ? (LEGACY_MAP[tabId] || null) : null;
      if (leg) {
        n.set('tab', leg.tab);
        n.set('sub', leg.sub);
      } else {
        n.set('tab', tabId);
        const sub = subId || DEFAULT_SUB[tabId];
        if (sub) n.set('sub', sub); else n.delete('sub');
      }
      return n;
    });
  }, [setSearchParams]);

  const setSub = useCallback((subId) => {
    setSearchParams((prev) => { const n = new URLSearchParams(prev); n.set('sub', subId); return n; });
  }, [setSearchParams]);

  const setSbom = useCallback((sbomId) => {
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      n.set('sbom', sbomId);
      if (sbomId) {
        const cur = n.get('tab') || 'overview';
        if (!MAIN_TAB_IDS.has(cur) || cur === 'history') {
          n.set('tab', 'overview');
          n.delete('sub');
        }
      }
      return n;
    });
  }, [setSearchParams]);

  const needsSbom = SBOM_REQUIRED.has(currentKey);

  const renderEmptyState = () => (
    <EmptyState
      icon={EMPTY_ICONS[currentKey] || '📭'}
      title={t('repoDetails.emptyStateTitle')}
      description={t(`repoDetails.emptyStates.${EMPTY_I18N_KEY[currentKey] || 'overview'}`)}
      size="sm"
    />
  );

  useEffect(() => {
    let cancelled = false;
    setSbomsLoading(true);
    axios.get(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/list`)
      .then((res) => { if (!cancelled) setSboms(res.data || []); })
      .catch(() => { if (!cancelled) setSboms([]); })
      .finally(() => { if (!cancelled) setSbomsLoading(false); });
    return () => { cancelled = true; };
  }, [repoName]);

  // Auto-select latest SBOM and default to overview tab on first load
  useEffect(() => {
    if (normalizedSboms.length === 0) return;
    const latestSbomId = normalizedSboms[0].CommitHash;
    const selectedExists = normalizedSboms.some((s) => s.CommitHash === selectedSbomId);
    const needsSbomSelect = !selectedSbomId || !selectedExists;
    const needsTabSet = !hasExplicitTab;
    if (!needsSbomSelect && !needsTabSet) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (needsSbomSelect) next.set('sbom', latestSbomId);
      if (needsTabSet) { next.set('tab', 'overview'); next.delete('sub'); }
      return next;
    });
  }, [selectedSbomId, normalizedSboms, hasExplicitTab, setSearchParams]);

  // Redirect legacy URLs in the address bar
  useEffect(() => {
    if (resolved) {
      setSearchParams((prev) => {
        const n = new URLSearchParams(prev);
        n.set('tab', resolved.tab);
        n.set('sub', resolved.sub);
        return n;
      });
    }
  }, [resolved, setSearchParams]);

  // ── Shared data fetch: load all 6 endpoints once per SBOM ───────────────────
  useEffect(() => {
    if (!effectiveSelectedSbomId || !repoName) {
      setSharedData(SHARED_EMPTY);
      return;
    }
    setSharedData(SHARED_EMPTY); // reset while loading
    let cancelled = false;
    const base = `${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${effectiveSelectedSbomId}-sbom.json`;
    const endpoints = {
      vulns:       `${base}/vulnerabilities`,
      health:      `${base}/health`,
      licenses:    `${base}/licenses`,
      quality:     `${base}/quality`,
      compliance:  `${base}/compliance`,
      remediation: `${base}/remediation`,
    };
    const keys = Object.keys(endpoints);
    Promise.allSettled(keys.map(k => axios.get(endpoints[k]))).then(results => {
      if (cancelled) return;
      const next = {};
      keys.forEach((k, i) => {
        next[k] = results[i].status === 'fulfilled' ? results[i].value.data : null;
      });
      setSharedData(next);
    });
    return () => { cancelled = true; };
  }, [effectiveSelectedSbomId, repoName]);

  const subTabList = SUB_TABS[activeTab] || [];

  return (
    <div>
      {/* ── Main tab bar ── */}
      <div className="ce-main-tabs-bar">
        {MAIN_TABS.map((tab) => (
          <button
            key={tab.id}
            className={`ce-main-tab-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setTab(tab.id)}
          >
            <span className="ce-main-tab-icon">{tab.icon}</span>
            <span>{t(tab.labelKey)}</span>
          </button>
        ))}
      </div>

      {/* ── Sub-tab bar ── */}
      {subTabList.length > 0 && (
        <div className="ce-sub-tabs-bar">
          {subTabList.map((sub) => (
            <button
              key={sub.id}
              className={`ce-sub-tab-btn${activeSub === sub.id ? ' active' : ''}`}
              onClick={() => setSub(sub.id)}
            >
              <span>{sub.icon}</span>
              <span>{t(sub.labelKey)}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── SBOM selector (only for views that need it) ── */}
      {needsSbom && (
        <div className="ce-sbom-bar">
          <span className="text-secondary fw-semibold" style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
            {t('repoDetails.selectorLabel')}
          </span>
          {sbomsLoading ? (
            <span className="small text-secondary fst-italic">Loading SBOMs…</span>
          ) : normalizedSboms.length > 0 ? (
            <select
              className="ce-filter-select"
              style={{ maxWidth: 360 }}
              value={effectiveSelectedSbomId}
              onChange={(e) => setSbom(e.target.value)}
            >
              <option value="">{t('repoDetails.selectorPlaceholder')}</option>
              {normalizedSboms.map((s) => (
                <option key={s.CommitHash} value={s.CommitHash}>
                  {s.CommitHash.substring(0, 20)}…
                </option>
              ))}
            </select>
          ) : (
            <span className="small text-secondary fst-italic">{t('repoDetails.selectorEmptyHint')}</span>
          )}
          {normalizedSboms.length > 0 && !effectiveSelectedSbomId && (
            <span style={{ fontSize: '0.8125rem', color: 'var(--bs-secondary-color)', fontStyle: 'italic', marginLeft: '0.5rem' }}>
              {t('repoDetails.selectorPrompt')}
            </span>
          )}
          {effectiveSelectedSbomId && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <a
                href={authUrl(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(selectedSbomFileName)}/evidence-pack`)}
                className="ce-btn ce-btn--secondary ce-btn--sm"
              >
                📦 Evidence Pack
              </a>
              <button
                className="ce-btn ce-btn--secondary ce-btn--sm"
                onClick={() => setShowPRModal(true)}
              >
                🐙 {t('githubPR.buttonLabel', 'Post to GitHub PR')}
              </button>
              <a
                href={authUrl(`${config.BASE_URL}/sbom/${encodeURIComponent(repoName)}/${encodeURIComponent(selectedSbomFileName)}/report`)}
                target="_blank"
                rel="noopener noreferrer"
                className="ce-btn ce-btn--primary ce-btn--sm"
              >
                📄 Export Report
              </a>
            </div>
          )}
        </div>
      )}

      {/* ── Workflow progress ── */}
      {effectiveSelectedSbomId && (
        <WorkflowProgress hasSbom={true} hasHealth={!!healthStats} hasVulns={hasVulns} hasPolicy={hasPolicy} />
      )}

      {/* ── Tab content ── */}
      <div className="tab-content p-3">

        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><AnalysisOverview repoName={repoName} sbomId={effectiveSelectedSbomId} onNavigateTab={setTab} preloadedData={sharedData} /></ErrorBoundary>
            : renderEmptyState()
        )}

        {/* VULNERABILITIES */}
        {activeTab === 'vulnerabilities' && activeSub === 'cves' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><VulnerabilityView repoName={repoName} sbomId={effectiveSelectedSbomId} onDataLoaded={() => setHasVulns(true)} initialData={sharedData.vulns || undefined} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'vulnerabilities' && activeSub === 'malware' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><MalwareView repoName={repoName} commitHash={effectiveSelectedSbomId} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'vulnerabilities' && activeSub === 'csaf' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><CsafView repoName={repoName} sbomId={effectiveSelectedSbomId} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'vulnerabilities' && activeSub === 'reachability' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><ReachabilityView repoName={repoName} sbomId={effectiveSelectedSbomId} sbomName={selectedSbomFileName} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'vulnerabilities' && activeSub === 'vex' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><VexView repoName={repoName} commitHash={effectiveSelectedSbomId} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'vulnerabilities' && activeSub === 'remediation' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><RemediationView repoName={repoName} commitHash={effectiveSelectedSbomId} /></ErrorBoundary>
            : renderEmptyState()
        )}

        {/* DEPENDENCIES */}
        {activeTab === 'dependencies' && activeSub === 'health' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><PackageHealthView repoName={repoName} sbomId={effectiveSelectedSbomId} onHealthData={handleHealthData} initialData={sharedData.health || undefined} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'dependencies' && activeSub === 'anomalies' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><AnomalyDetectionView repoName={repoName} sbomId={effectiveSelectedSbomId} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'dependencies' && activeSub === 'graph' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><DependencyGraph repoName={repoName} sbomId={effectiveSelectedSbomId} /></ErrorBoundary>
            : renderEmptyState()
        )}

        {/* COMPLIANCE */}
        {activeTab === 'compliance' && activeSub === 'licenses' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><LicenseView repoName={repoName} sbomId={effectiveSelectedSbomId} initialData={sharedData.licenses || undefined} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'compliance' && activeSub === 'policy' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><PolicyView repoName={repoName} sbomId={effectiveSelectedSbomId} sbomName={selectedSbomFileName} onDataLoaded={() => setHasPolicy(true)} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'compliance' && activeSub === 'cra' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><CraReportView repoName={repoName} sbomId={effectiveSelectedSbomId} sbomName={selectedSbomFileName} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'compliance' && activeSub === 'slsa' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><SlsaProvenanceView repoName={repoName} sbomId={effectiveSelectedSbomId} sbomName={selectedSbomFileName} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'compliance' && activeSub === 'frameworks' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><ComplianceView repoName={repoName} sbomId={effectiveSelectedSbomId} /></ErrorBoundary>
            : renderEmptyState()
        )}

        {/* CODE ANALYSIS */}
        {activeTab === 'codeanalysis' && activeSub === 'intelligence' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><IntelligenceView repoName={repoName} sbomId={effectiveSelectedSbomId} sbomName={selectedSbomFileName} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'codeanalysis' && activeSub === 'codeql' && (
          <ErrorBoundary><RepoCodeAnalysis repoName={repoName} /></ErrorBoundary>
        )}
        {activeTab === 'codeanalysis' && activeSub === 'sast' && (
          <ErrorBoundary><RepoSastView repoName={repoName} /></ErrorBoundary>
        )}

        {/* HISTORY */}
        {activeTab === 'history' && activeSub === 'quality' && (
          effectiveSelectedSbomId
            ? <ErrorBoundary><SbomQuality repoName={repoName} sbomName={effectiveSelectedSbomId} initialData={sharedData.quality || undefined} /></ErrorBoundary>
            : renderEmptyState()
        )}
        {activeTab === 'history' && activeSub === 'trend' && (
          <ErrorBoundary><TrendView repoName={repoName} /></ErrorBoundary>
        )}
        {activeTab === 'history' && activeSub === 'diff' && (
          <ErrorBoundary><SbomDiff repoName={repoName} sboms={normalizedSboms} /></ErrorBoundary>
        )}
        {activeTab === 'history' && activeSub === 'sboms' && (
          <>
            {!sbomsLoading && sboms.length === 0 && (
              <div style={{ background: 'var(--accent-light)', border: '1px solid var(--accent)', borderRadius: 'var(--radius-sm)', padding: '0.875rem 1.125rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                <span style={{ fontSize: '1.5rem' }}>💡</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--accent-text)' }}>No SBOMs yet for this repository</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--bs-secondary-color)', marginTop: '0.2rem' }}>Upload an SBOM to start vulnerability, license, and SBOM quality analysis.</div>
                </div>
                <Link to="/upload" className="btn btn-sm ms-auto" style={{ background: 'var(--accent)', color: '#fff', whiteSpace: 'nowrap' }}>⬆️ Upload SBOM</Link>
              </div>
            )}
            <ErrorBoundary>
              <SbomList
                repoName={repoName}
                onSbomsLoaded={setSboms}
                onMalwareScan={(hash) => { setSbom(hash); setTab('vulnerabilities', 'malware'); }}
              />
            </ErrorBoundary>
          </>
        )}

      </div>

      {/* AI Assistant Panel */}
      <AiAssistantPanel repoName={repoName} sbomId={effectiveSelectedSbomId} healthStats={healthStats} />

      <GitHubPRCommentModal
        open={showPRModal}
        onClose={() => setShowPRModal(false)}
        repoName={repoName}
        sbomId={effectiveSelectedSbomId}
      />
    </div>
  );
}

export default RepoDetailsTabs;
